import React, { useState, useEffect } from 'react';
import styles from './KeyCompetitorsV2.module.scss';
import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import TopRightArrowIcon from '../../../../../../assets/Svg/TopRightArrowIcon';
import { getFaviconUrl, getGoogleFaviconUrl, isValidWebsiteUrl } from '../../../../../../utils/urlMetadata';
import A4Page from '../../../../../AicaPDFReport/A4Page/A4Page';
import { reportSectionsToShow } from '../../../../../AicaPDFReport/AicaPDFReport';
import { REPORT_SECTION_NAMES } from '../../../../../../store/pdfReport/pdfReport_constants';
import HeaderBodyBox from '../../../../../../common/HeaderBodyBox';
import clsx from 'clsx';

interface AdditionalDetails {
    [key: string]: any;
}

interface Competitor {
    companyName: string;
    description: string;
    website: string;
    location: string;
    revenue: string;
    employees: string;
    foundingYear: string;
    totalFunding: string;
    fundingStage: string;
    topInvestors: string[];
    keyAchievements: string;
    additionalDetails?: AdditionalDetails;
}

interface KeyCompetitorsV2Props {
    competitors: Competitor[];
    isPDFReport?: boolean;
}

interface CompanyMetadata {
    image?: string;
    favicon?: string;
}

interface CompanyMetadataMap {
    [url: string]: CompanyMetadata;
}

// Logo with a graceful fallback chain: Google favicon -> site's own favicon ->
// first-letter placeholder. Each source is tried in turn as the previous one
// errors, so a blocked or missing logo source never leaves an empty box.
const CompetitorLogo: React.FC<{ name: string; image?: string; favicon?: string }> = ({ name, image, favicon }) => {
    const sources = [favicon, image].filter(Boolean) as string[];
    const [sourceIndex, setSourceIndex] = useState(0);
    const currentSource = sources[sourceIndex];

    if (!currentSource) {
        return <div className={styles.placeholderImg}>{name?.charAt(0) || ''}</div>;
    }

    return <img src={currentSource} alt={name} onError={() => setSourceIndex((index) => index + 1)} />;
};

const KeyCompetitorsV2: React.FC<KeyCompetitorsV2Props> = ({ competitors, isPDFReport = false }) => {
    const [websiteMetadata, setWebsiteMetadata] = useState<CompanyMetadataMap>({});

    useEffect(() => {
        const metadataMap: CompanyMetadataMap = {};

        competitors.forEach((competitor) => {
            if (competitor.website?.trim()) {
                metadataMap[competitor.website] = {
                    image: getGoogleFaviconUrl(competitor.website),
                    favicon: getFaviconUrl(competitor.website),
                };
            }
        });

        setWebsiteMetadata(metadataMap);
    }, [competitors]);

    if (!competitors || competitors.length === 0) {
        return null;
    }

    // Function to get custom JSX/CSS for specific columns
    const getRowText = (rowKey: string, value: any, competitor: Competitor): React.ReactNode => {
        switch (rowKey) {
            case 'companyName':
                const metadata = websiteMetadata[competitor.website] || {};

                if (isPDFReport) {
                    return (
                        <div className={styles.companyNameCell}>
                            <div className={styles.companyImage}>
                                <CompetitorLogo name={value} image={metadata.image} favicon={metadata.favicon} />
                            </div>
                            <div className={styles.companyLink} title={value} style={{ marginTop: '8px', textAlign: 'center' }}>
                                {value}
                            </div>
                        </div>
                    );
                }

                const isValidUrl = competitor.website && isValidWebsiteUrl(competitor.website);

                return (
                    <div className={styles.companyNameCell}>
                        <div className={styles.companyImage}>
                            <CompetitorLogo name={value} image={metadata.image} favicon={metadata.favicon} />
                        </div>
                        {isValidUrl ? (
                            <a
                                href={competitor.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`${styles.companyLink} ${styles.hasWebsite}`}
                                title={value}
                            >
                                {value}
                                <span className={styles.arrowIcon}>
                                    <TopRightArrowIcon width={14} height={14} style={{ display: 'block' }} />
                                </span>
                            </a>
                        ) : (
                            <div className={styles.companyLink} title={value}>
                                {value}
                            </div>
                        )}
                    </div>
                );
            case 'revenue':
                return <span style={{ fontWeight: '600', color: '#059669' }}>{value}</span>;
            case 'topInvestors':
                if (isPDFReport) {
                    return (
                        <div className={styles.topInvestors}>
                            {Array.isArray(value) && value.length > 0 ? (
                                <div style={{ color: '#000000' }}>{value.join(', ')}</div>
                            ) : !!value?.[0] ? (
                                <div style={{ color: '#000000' }}>{value[0]}</div>
                            ) : (
                                <span>-</span>
                            )}
                        </div>
                    );
                }

                return (
                    <div className={styles.topInvestors}>
                        {Array.isArray(value) && value.length > 0 ? (
                            <>
                                <span className={styles.firstInvestor} title={value[0]}>
                                    {value[0]}
                                </span>
                                {value.length > 1 && (
                                    <Tooltip
                                        direction="top"
                                        style={{ top: '-3.5rem', left: '1.2rem' }}
                                        fitContent={true}
                                        content={
                                            <div>
                                                {value.slice(1).map((investor, index) => (
                                                    <div key={index}>{investor}</div>
                                                ))}
                                            </div>
                                        }
                                    >
                                        <span className={styles.investor}>{` + ${value.length - 1} more`}</span>
                                    </Tooltip>
                                )}
                            </>
                        ) : !!value?.[0] ? (
                            <span className={styles.firstInvestor} title={value[0]}>
                                {value[0]}
                            </span>
                        ) : (
                            <span>-</span>
                        )}
                    </div>
                );
            default:
                return value;
        }
    };

    const detailFields = [
        { key: 'foundingYear', label: 'Founded' },
        { key: 'location', label: 'Location' },
        { key: 'fundingStage', label: 'Funding Stage' },
    ];

    const renderCompetitorCards = () => (
        <div className={styles.competitorsContent}>
            <div className={styles.cardsContainer}>
                <div className={styles.cardsGrid}>
                    {competitors.map((competitor, index) => (
                        <div key={index} className={clsx(styles.competitorCard, { [styles.firstCompetitorCard]: index === 0 })}>
                            {getRowText('companyName', competitor.companyName, competitor)}

                            <p className={styles.cardOverview}>{competitor.description}</p>

                            <div className={styles.cardDetails}>
                                {detailFields.map((field) => {
                                    const value = competitor[field.key as keyof Competitor] as string;
                                    return (
                                        <div key={field.key} className={styles.detailRow}>
                                            <span className={styles.detailLabel}>{field.label}</span>
                                            <span className={styles.detailValue}>{value || '-'}</span>
                                        </div>
                                    );
                                })}
                            </div>

                            <p className={styles.keyAchievements}>
                                {competitor.keyAchievements ? (
                                    <>
                                        <span className={styles.keyAchievementsLabel}>Key Achievements: </span>
                                        {competitor.keyAchievements}
                                    </>
                                ) : (
                                    ''
                                )}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const render = (
        <HeaderBodyBox
            title="Key Competitors"
            isPDFReport={isPDFReport}
            className={clsx(styles.competitorsContainer, { [styles.PDFReport]: isPDFReport })}
            bodyClassName={styles.competitorsBody}
        >
            {renderCompetitorCards()}
        </HeaderBodyBox>
    );

    if (isPDFReport) return competitors.length && reportSectionsToShow.includes(REPORT_SECTION_NAMES.KEY_COMPETITORS) ? render : null;
    else return render;
};

export default KeyCompetitorsV2;
